import {
  Box,
  BoxProps,
  Flex,
  Skeleton,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { ProtectedImage } from "../ProtectedImage";
import { EmblaCarouselType } from "embla-carousel";
import { useNavigate } from "react-router";
import { useQuery, skipToken } from "@tanstack/react-query";
import {
  ContentSectionCustomCard,
  GCChannel,
  GCStream,
  Section,
  Release,
} from "../../types";
import { CustomCardFacet } from "./components/CustomCardFacet";
import { CONTEXT_ENTITY_TYPES } from "./constants";
import { useAccountData } from "../../hooks/useAccountData";
import { useUserData } from "../../hooks/useUserData";
import { CustomCardLeavingSoonBadge } from "./components/LeavingSoonBadge";
import { FUNNEL_TYPES } from "../funnel/utils/contants";
import { FunnelContextArea } from "../funnel/types";
import { useNavigateToReaderWithContext } from "../../screens/reader/hooks/useNavigateToReaderWithContext";
import { useToggleArtistFollow } from "./hooks/useToggleArtistFollow";
import { LockedContentBadge } from "./components/LockedContentBadge";
import { PurchasedContentBadge } from "./components/PurchasedContentBadge";
import { setFunnelModalConfig } from "../funnel/FunnelModal.tsx";
import { SaleBadge } from "./components/SaleBadge";
import { useReleasePricingInfo } from "../../hooks/useReleasePricingInfo";
import { memo } from "react";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { trackRecsysCardItemClick } from "../../analytics/recsys/tracking";

export interface CustomCardProps extends BoxProps {
  coverImage: string;
  contentCard: ContentSectionCustomCard;
  isHidden?: boolean;
  emblaApi?: EmblaCarouselType;
  cardResponsiveWidths?: Record<string, string> | string;
  cardResponsiveHeights?: Record<string, string> | string;
  cardResponsiveBorderRadius?: Record<string, string>;
  fetchPriority?: "high" | "low";
  index?: number;
  sectionIndex?: number;
  section?: Section;
}

const defaultCardResponsiveHeights: Record<string, string> = {
  base: "260px",
  sm: "336px",
  md: "421px",
  lg: "398px",
  xl: "398px",
  "2xl": "437px",
  "3xl": "555px",
  "4xl": "828px",
};

export const CustomCard = memo(
  ({
    coverImage,
    contentCard,
    isHidden = false,
    emblaApi,
    cardResponsiveWidths,
    cardResponsiveHeights,
    cardResponsiveBorderRadius,
    fetchPriority,
    index,
    sectionIndex,
    section,
    ...props
  }: CustomCardProps) => {
    const defaultCardResponsiveWidths = {
      base: "260px",
      sm: "336px",
      md: "632px",
      lg: "598px",
      xl: "708px",
      "2xl": "777px",
      "3xl": "988px",
      "4xl": "1472px",
    };

    const defaultCardBorderRadius = {
      base: "16px",
      md: "17px",
      lg: "18px",
      xl: "18px",
      "2xl": "20px",
      "3xl": "25px",
      "4xl": "38px",
    };

    const cardWidths = cardResponsiveWidths || defaultCardResponsiveWidths;
    const cardHeights = cardResponsiveHeights || defaultCardResponsiveHeights;
    const cardBorderRadius =
      cardResponsiveBorderRadius || defaultCardBorderRadius;

    // this is needed to fix a border radius issue on the facet ribbon
    const cardOverlayBorderRadius: Record<string, string> = {};
    for (const [key, value] of Object.entries(cardBorderRadius)) {
      cardOverlayBorderRadius[key] = `calc(${value} - 1px)`;
    }

    const isRowFeatureCard = contentCard.card_type === "featured-row-carousel";
    const isFeatureRoadblockCard =
      contentCard.card_type === "featured-roadblock-carousel";
    const isFeatureOrRoadblockCard = isRowFeatureCard || isFeatureRoadblockCard;

    const navigate = useNavigate();
    const { goToCTAUrl, goToCTAUrlWithContext } = useNavigateToReaderWithContext({
      comicToNavigate:
        contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.comic &&
        contentCard.comic
          ? (contentCard.comic ?? undefined)
          : undefined,
      releaseToNavigate:
        contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.release &&
        contentCard.release
          ? (contentCard.release ?? undefined)
          : undefined,
      cardPosition: index,
      sectionPosition: sectionIndex,
      section,
    });
    const accountData = useAccountData();
    const pricingInfo = useReleasePricingInfo(contentCard.release ?? undefined);

    const creatorCacheKey = `${contentCard.artist?.entity_type}-${contentCard.artist?.id}`;
    const { handleToggleArtistFollow } = useToggleArtistFollow(creatorCacheKey);
    const { openBundleDetails } = useSlideInMenu();
    const handleClickCard = () => {
      const impressionId =
        section && sectionIndex !== undefined
          ? trackRecsysCardItemClick({
              section,
              sectionIndex,
              item: contentCard,
              itemIndex: index ?? 0,
            })
          : undefined;

      if (contentCard.cta_type === "url" && contentCard.cta_url) {
        if (contentCard.cta_url.includes("/stream")) {
          const channels = accountData?.home_tab_channels;
          const streams = channels?.flatMap(
            (channel: GCChannel) => channel.streams,
          );
          const urlChunks = contentCard.cta_url?.split("/") ?? [];
          const urlStreamId = urlChunks[urlChunks.length - 1];
          const newActiveChannel = streams?.find((stream: GCStream) => {
            return urlStreamId === stream.id;
          });

          navigate(`/channel/${newActiveChannel?.slug}`);
        } else if (contentCard.cta_url.includes("news/details")) {
          navigate(new URL(contentCard.cta_url).pathname);
        } else {
          window.location.href = contentCard.cta_url;
        }
      } else if (
        contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.artist &&
        contentCard.artist
      ) {
        if (contentCard.cta_type === "notify") {
          handleToggleArtistFollow();
        } else {
          navigate(new URL(contentCard.artist.url).pathname);
        }
      } else if (
        contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.comic &&
        contentCard.comic
      ) {
        // this check is needed because "notify" cta_type should not open the reader
        if (contentCard.cta_type === "notify" && contentCard.comic.url) {
          const context = {
            modalContext: FunnelContextArea.addToLibrary,
            cacheKey: `${contentCard.comic.entity_type}-${contentCard.comic.id}`,
          };
          if (userData) {
            setFunnelModalConfig({
              type: FUNNEL_TYPES.addToLibrary,
              context,
            });
          } else {
            setFunnelModalConfig({
              type: FUNNEL_TYPES.signup,
              context,
              options: {
                onSuccess: () => {
                  // After auth orchestration, show add to library modal
                  setFunnelModalConfig({
                    type: FUNNEL_TYPES.addToLibrary,
                    context,
                  });
                },
              },
            });
          }
        } else if (contentCard.comic.read_url) {
          if (impressionId) {
            goToCTAUrlWithContext({ impressionId });
          } else {
            goToCTAUrl();
          }
        }
      } else if (
        contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.release &&
        contentCard.release?.read_url
      ) {
        if (impressionId) {
          goToCTAUrlWithContext({ impressionId });
        } else {
          goToCTAUrl();
        }
      } else if (contentCard.bundle && contentCard.bundle.uuid) {
        openBundleDetails(contentCard.bundle.uuid);
      }
    };

    const handleClickHiddenCard = () => {
      emblaApi?.scrollNext();
    };

    const leaving_soon_date = contentCard?.comic?.leaving_soon_date;

    // Cache the release data so it can be updated after purchase
    const isComicCard =
      contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.comic;
    const releaseCacheKey =
      !isComicCard && contentCard.release
        ? `${contentCard.release.entity_type}-${contentCard.release.id}`
        : null;

    const { data: cachedRelease } = useQuery<Release>({
      queryKey: [releaseCacheKey],
      queryFn: skipToken,
      enabled: !!releaseCacheKey,
      networkMode: "offlineFirst",
    });

    const releaseToUse = cachedRelease ?? contentCard.release;
    const hasPurchased = releaseToUse?.release_user_purchase;

    // Calculate lock state with fresh cached data instead of using stale prop
    const { userData } = useUserData();
    const userIsGold = userData?.gold;
    const { release_user_purchase, premium_only, is_free } = releaseToUse ?? {};
    const isLockedCalculated =
      !isComicCard &&
      !is_free &&
      !release_user_purchase &&
      (!userIsGold || premium_only);

    const variant = isRowFeatureCard ? "featureCard" : "heroCard";

    const cardFacetStyles = useMultiStyleConfig("CustomCardFacet", { variant });

    return (
      <Flex
        direction="column"
        position="relative"
        w={cardWidths}
        h={cardHeights}
        {...props}
      >
        <ProtectedImage
          className={`image-with-priority`}
          loading={
            fetchPriority === "low"
              ? "lazy"
              : fetchPriority === "high"
                ? "eager"
                : undefined
          }
          src={coverImage}
          w={cardWidths}
          h={cardHeights}
          maxH="inherit"
          maxW="inherit"
          borderRadius={cardBorderRadius}
          objectFit="cover"
          onClick={
            !isHidden
              ? (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleClickCard();
                }
              : undefined
          }
          cursor="pointer"
          outline="1px solid"
          outlineColor="transparent.white.20"
          outlineOffset="-1px"
          alt={contentCard.cta_url ?? ""}
          fallback={
            <Skeleton
              w={cardWidths}
              h={cardHeights}
              borderRadius={cardBorderRadius}
            />
          }
        />
        <Box position="absolute" top={0} left={0}>
          {contentCard.comic_facet_group_option_id &&
            !leaving_soon_date &&
            !pricingInfo.isOnSale && (
              <CustomCardFacet
                borderRadius={
                  isRowFeatureCard
                    ? `${cardBorderRadius.base} 0 8px 0`
                    : cardBorderRadius
                }
                variant={variant}
                facetId={contentCard.comic_facet_group_option_id}
              />
            )}
          {leaving_soon_date && !pricingInfo.isOnSale && (
            <CustomCardLeavingSoonBadge
              leavingSoonDate={leaving_soon_date}
              borderRadius={
                isRowFeatureCard
                  ? `${cardBorderRadius.base} 0 8px 0`
                  : cardBorderRadius
              }
              fontSize={cardFacetStyles.text.fontSize as string}
              padding={!isRowFeatureCard ? "8px 16px 8px 16px" : undefined}
            />
          )}
        </Box>
        {pricingInfo.isOnSale && <SaleBadge releasePricingInfo={pricingInfo} />}
        {isFeatureOrRoadblockCard && isLockedCalculated ? (
          <LockedContentBadge position="absolute" bottom={0} left="-1px" />
        ) : isFeatureOrRoadblockCard && !isLockedCalculated && hasPurchased ? (
          <PurchasedContentBadge position="absolute" bottom={0} left="-1px" />
        ) : null}
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          bg="transparent.black.60"
          borderRadius={cardOverlayBorderRadius}
          cursor="pointer"
          opacity={isHidden && !isRowFeatureCard ? 1 : 0}
          transition="opacity 0.5s ease"
          pointerEvents={isHidden && !isRowFeatureCard ? "auto" : "none"}
          onClick={handleClickHiddenCard}
        />
      </Flex>
    );
  },
  (prevProps, nextProps) => {
    // Return true if props are equal (skip re-render), false if different (re-render)
    // Only compare primitive values - object comparisons (section, etc.) would always fail
    return (
      prevProps.contentCard.id === nextProps.contentCard.id &&
      prevProps.contentCard.comic?.leaving_soon_date ===
        nextProps.contentCard.comic?.leaving_soon_date &&
      prevProps.contentCard.comic_facet_group_option_id ===
        nextProps.contentCard.comic_facet_group_option_id &&
      prevProps.contentCard.release?.release_user_purchase ===
        nextProps.contentCard.release?.release_user_purchase &&
      prevProps.contentCard.release?.ppb_price_point ===
        nextProps.contentCard.release?.ppb_price_point &&
      prevProps.contentCard.release?.ppb_sale_price_point ===
        nextProps.contentCard.release?.ppb_sale_price_point &&
      prevProps.coverImage === nextProps.coverImage &&
      prevProps.isHidden === nextProps.isHidden && // Animation depends on this!
      prevProps.fetchPriority === nextProps.fetchPriority &&
      prevProps.index === nextProps.index &&
      prevProps.sectionIndex === nextProps.sectionIndex
    );
  },
);

CustomCard.displayName = "CustomCard";
